// 📄 src/modules/LocationMovements/adapters/LocationApiAdapter.ts

import { HttpClient } from '@adapters/HttpClient';
import { Location } from '../models/Locations';

export interface LocationFilters {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface LocationPage {
  locations: Location[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

export class LocationApiAdapter {
  constructor(private httpClient: HttpClient) {}

  private static readonly DEFAULT_FILTERS = {
    sortBy: 'id',
    sortDirection: 'desc' as 'asc' | 'desc',
    page: 1,
    limit: 2000,
  };

  /**
   * Obtiene ubicaciones paginadas desde el endpoint /location.
   */
  async getLocationsPaginated(
    filters: LocationFilters = {}
  ): Promise<LocationPage> {
    const {
      sortBy = LocationApiAdapter.DEFAULT_FILTERS.sortBy,
      sortDirection = LocationApiAdapter.DEFAULT_FILTERS.sortDirection,
      page = LocationApiAdapter.DEFAULT_FILTERS.page,
      limit = LocationApiAdapter.DEFAULT_FILTERS.limit,
      // otros filtros si hicieran falta
    } = filters;

    const params: Record<string, any> = {
      order_by: sortBy,
      order_direction: sortDirection,
      page,
      per_page: limit,
    };

    const response = await this.httpClient.get<{
      data: any[];
      meta: {
        total: number;
        perPage: number;
        currentPage: number;
        lastPage: number;
      };
      links?: {
        first: string;
        prev: string | null;
        next: string | null;
        last: string;
      };
    }>('/locations', params);

    const rawArray = response.data;
    const locations: Location[] = rawArray as Location[];

    const meta = response.meta;
    const currentPage = meta.currentPage ?? (meta as any).current_page;
    const lastPage = meta.lastPage ?? (meta as any).last_page;
    const total = meta.total;

    return {
      locations,
      nextPage:
        typeof currentPage === 'number' &&
        typeof lastPage === 'number' &&
        currentPage < lastPage
          ? currentPage + 1
          : undefined,
      hasMore:
        typeof currentPage === 'number' && typeof lastPage === 'number'
          ? currentPage < lastPage
          : false,
      total,
    };
  }

  async getAllLocations(): Promise<Location[]> {
    const firstPage = await this.getLocationsPaginated({
      page: 1,
      limit: Number.MAX_SAFE_INTEGER,
    });
    return firstPage.locations;
  }
}
